import { prepareAxiosSentryEvent } from "@/utils/sentryApiErrors";
import { scrubJointCosignToken } from "@/utils/jointCosigner";

function eventMessages(event, hint) {
  return [
    hint?.originalException?.message,
    event?.message,
    ...(event?.exception?.values || []).map(value => value?.value),
  ].filter(Boolean).join(" ");
}

function eventFrames(event) {
  return (event?.exception?.values || [])
    .flatMap(value => value?.stacktrace?.frames || []);
}

export function hasFirstPartyFrame(event) {
  return eventFrames(event).some(frame => {
    const filename = String(frame?.filename || "");
    return (
      frame?.in_app === true ||
      filename.includes("webpack://invown/src/") ||
      filename.includes("/src/") ||
      filename.includes("invown.com/app/") ||
      filename.includes("dev.invown.com/app/")
    );
  });
}

function hasFirstPartySourceFrame(event) {
  return eventFrames(event).some(frame => {
    const filename = String(frame?.filename || "");
    return (
      frame?.in_app === true ||
      filename.includes("webpack://invown/src/") ||
      filename.includes("/src/")
    );
  });
}

function hasThirdPartyFrame(event, patterns) {
  return eventFrames(event).some(frame => {
    const filename = String(frame?.filename || "");
    return patterns.some(pattern => pattern.test(filename));
  });
}

export function hasExtensionFrame(event) {
  return eventFrames(event).some(frame => {
    const filename = String(frame?.filename || "");
    return (
      filename.startsWith("chrome-extension://") ||
      filename.startsWith("moz-extension://") ||
      filename.startsWith("safari-extension://")
    );
  });
}

export function isMetaMaskNoise(event, hint) {
  const messages = eventMessages(event, hint);

  return hasExtensionFrame(event) && /metamask|failed to connect to metamask|extension not found/i.test(messages);
}

export function isExpectedRouterNavigationFailure(event, hint) {
  const messages = eventMessages(event, hint);

  return /Redirected when going from|Avoided redundant navigation|Navigation cancelled|Navigation aborted/i.test(messages);
}

export function isResizeObserverLoopError(event, hint) {
  return /ResizeObserver loop (limit exceeded|completed with undelivered notifications)/i
    .test(eventMessages(event, hint));
}

export function isThirdPartyScriptNoise(event) {
  if (hasFirstPartyFrame(event)) {
    return false;
  }

  return hasThirdPartyFrame(event, [
    /google\.com\/recaptcha/i,
    /gstatic\.com\/recaptcha/i,
    /recaptcha/i,
    /livechatinc\.com/i,
    /cdn\.livechatinc\.com/i,
  ]);
}

export function isExtensionScriptNoise(event) {
  return hasExtensionFrame(event) && !hasFirstPartyFrame(event);
}

export function isNonActionableNetworkNoise(event, hint) {
  if (hasFirstPartyFrame(event) || hint?.originalException?.isAxiosError) {
    return false;
  }

  return /\b(Failed to fetch|NetworkError|Load failed|The network connection was lost)\b/i
    .test(eventMessages(event, hint));
}

export function isUnleashMetricsNoise(event, hint) {
  const messages = eventMessages(event, hint);
  if (!/\b(Failed to fetch|NetworkError|Network Error|Load failed)\b/i.test(messages)) {
    return false;
  }

  return eventFrames(event).some(frame => {
    const filename = String(frame?.filename || "");
    const functionName = String(frame?.function || frame?.functionName || "");
    return (
      /unleash-proxy-client/i.test(filename) ||
      /unleash.*metrics|metrics\.js/i.test(filename) ||
      /sendMetrics|countMetrics|bucketMetrics/i.test(functionName)
    );
  });
}

export function isBooleanPromiseRejection(event, hint) {
  if (hasFirstPartyFrame(event)) {
    return false;
  }

  const original = hint?.originalException;
  if (original === true || original === false) {
    return true;
  }

  return /Non-Error promise rejection captured with value: (true|false)|UnhandledRejection.*\b(true|false)\b/i
    .test(eventMessages(event, hint));
}

export function isBrowserObjectLookupPromiseNoise(event, hint) {
  if (hasFirstPartyFrame(event)) {
    return false;
  }

  return /Object Not Found Matching Id:\s*\d+/i.test(eventMessages(event, hint));
}

export function isInjectedNativeBridgeNoise(event, hint) {
  if (hasFirstPartySourceFrame(event)) {
    return false;
  }

  return /No Listener:\s*tabs:outgoing\.message\.ready|window\.webkit\.messageHandlers|WKWebView API client did not respond|runtime\.sendMessage\(\)\. Tab not found|sendDataToNative/i
    .test(eventMessages(event, hint));
}

function markLowPriorityEvent(event, reason) {
  return {
    ...event,
    level: "warning",
    tags: {
      ...(event.tags || {}),
      low_priority_browser_error: "true",
      low_priority_reason: reason,
    },
    contexts: {
      ...(event.contexts || {}),
      browser_noise: {
        reason,
      },
    },
  };
}

function markFrontendEvent(event) {
  return {
    ...event,
    tags: {
      ...(event.tags || {}),
      surface: "frontend",
    },
  };
}

export function isChunkLoadError(errorOrEvent) {
  const message = String(
    errorOrEvent?.message ||
    errorOrEvent?.reason?.message ||
    errorOrEvent?.error?.message ||
    errorOrEvent?.target?.href ||
    errorOrEvent?.target?.src ||
    (errorOrEvent?.exception?.values || []).map(value => value?.value).join(" ") ||
    ""
  );

  return /ChunkLoadError|Loading CSS chunk .* failed|Loading chunk .* failed|\.css\)$|\.js\)$/.test(message);
}

function scrubValue(value) {
  return typeof value === "string" ? scrubJointCosignToken(value) : value;
}

// The joint co-sign token (checkout-joint-cosigner story) must never reach
// Sentry: scrub URLs, Referer, breadcrumbs, transaction names and tags.
export function scrubJointCosignEvent(event) {
  if (!event || typeof event !== "object") return event;
  if (event.request) {
    event.request.url = scrubValue(event.request.url);
    if (event.request.query_string) {
      event.request.query_string = scrubValue(
        typeof event.request.query_string === "string"
          ? event.request.query_string
          : JSON.stringify(event.request.query_string),
      );
    }
    if (event.request.headers) {
      Object.keys(event.request.headers).forEach((key) => {
        event.request.headers[key] = scrubValue(event.request.headers[key]);
      });
    }
  }
  event.transaction = scrubValue(event.transaction);
  if (event.tags) {
    Object.keys(event.tags).forEach((key) => {
      event.tags[key] = scrubValue(event.tags[key]);
    });
  }
  (event.breadcrumbs || []).forEach((crumb) => {
    if (!crumb) return;
    crumb.message = scrubValue(crumb.message);
    if (crumb.data) {
      ["url", "from", "to"].forEach((key) => {
        crumb.data[key] = scrubValue(crumb.data[key]);
      });
    }
  });
  (event.spans || []).forEach((span) => {
    if (span) span.description = scrubValue(span.description);
  });
  return event;
}

// PII scrubbing (mirrors app/utils/pii_scrub.py on the backend). Values
// whose key names a tax ID, account or routing number, date of birth,
// token, password or secret become "[Filtered]"; SSN- and EIN-shaped
// numbers in free text are replaced too.
export const PII_FILTERED = "[Filtered]";

// Keep in sync with app/utils/pii_scrub.py.
const STRONG_KEY_TOKENS = new Set([
  "ssn", "tin", "ein", "itin", "dob",
  "password", "passphrase", "secret",
  "routing", "authorization", "cookie",
  "iban", "aba", "dek", "kek",
  "idnumber", "accountno", "acctno", "accountnumber", "acctnumber",
  "accountnum", "acctnum",
  "taxpayer", "fein", "birthday", "cvv", "pan", "card", "dda",
]);
const STRONG_KEY_RUNS = [
  ["social", "security"],
  ["tax", "id"],
  ["taxid"],
  ["account", "number"],
  ["acct", "number"],
  ["account", "no"],
  ["acct", "no"],
  ["account", "num"],
  ["acct", "num"],
  ["account", "full"],
  ["routing", "number"],
  ["birth", "date"],
  ["birthdate"],
  ["date", "of", "birth"],
  ["api", "key"],
  ["apikey"],
  ["card", "number"],
  ["cardnumber"],
  ["private", "key"],
  ["privatekey"],
  ["id", "number"],
];
// Sensitive only when they end the key: `account`, `csrf_token` are;
// `account_id`, `token_count` are not.
const WEAK_LAST_TOKENS = new Set(["token", "account", "acct", "bankaccount", "bankacct"]);
const EXEMPT_LAST_TOKENS = new Set(["last4", "masked", "mask"]);
const EXEMPT_FIRST_TOKENS = new Set(["has", "is"]);
const NUMERIC_LAST_TOKENS = new Set([
  "cents", "amount", "amounts", "total", "price", "count", "timestamp",
  "ts", "epoch", "ms", "seconds", "duration", "bytes", "size",
]);
// SSN, EIN or a bare nine-digit run; not inside a longer alphanumeric run
// and not part of a decimal number.
const TAX_ID_PATTERN =
  /(^|[^0-9A-Za-z.]|(?:^|[^\d])\.)(\d{3}[- ]\d{2}[- ]\d{4}|\d{2}-\d{7}|\d{9})(?![0-9A-Za-z])(?!\.\d)/g;
const PLAIN_NUMBER = /^-?\d+(?:\.\d+)?$/;

function keyTokens(key) {
  return key
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/([A-Z])([A-Z][a-z])/g, "$1_$2")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
}

function hasRun(tokens, run) {
  for (let i = 0; i + run.length <= tokens.length; i += 1) {
    if (run.every((part, j) => tokens[i + j] === part)) return true;
  }
  return false;
}

export function isSensitiveKey(key) {
  if (typeof key !== "string" || !key) return false;
  const tokens = keyTokens(key);
  if (!tokens.length) return false;
  if (EXEMPT_LAST_TOKENS.has(tokens[tokens.length - 1]) || EXEMPT_FIRST_TOKENS.has(tokens[0])) {
    return false;
  }
  if (tokens.some((token) => STRONG_KEY_TOKENS.has(token))) return true;
  if (STRONG_KEY_RUNS.some((run) => hasRun(tokens, run))) return true;
  return WEAK_LAST_TOKENS.has(tokens[tokens.length - 1]);
}

function isNumericKey(key) {
  if (typeof key !== "string" || !key) return false;
  const tokens = keyTokens(key);
  return tokens.length > 0 && NUMERIC_LAST_TOKENS.has(tokens[tokens.length - 1]);
}

export function scrubPiiText(value) {
  if (typeof value !== "string" || !value) return value;
  return value.replace(TAX_ID_PATTERN, (_match, lead) => `${lead}${PII_FILTERED}`);
}

function scrubQueryString(value) {
  if (typeof value !== "string" || !value) return value;
  const parts = value.split("&").map((pair) => {
    const eq = pair.indexOf("=");
    if (eq < 0) return pair;
    let key = pair.slice(0, eq);
    try {
      key = decodeURIComponent(key.replace(/\+/g, " "));
    } catch (_error) {
      // Keep the raw key.
    }
    return isSensitiveKey(key) ? `${pair.slice(0, eq)}=${encodeURIComponent(PII_FILTERED)}` : pair;
  });
  return scrubPiiText(parts.join("&"));
}

function scrubPiiUrl(value) {
  if (typeof value !== "string" || !value.includes("?")) return scrubPiiText(value);
  const qIndex = value.indexOf("?");
  const base = value.slice(0, qIndex);
  const rest = value.slice(qIndex + 1);
  const hashIndex = rest.indexOf("#");
  const query = hashIndex < 0 ? rest : rest.slice(0, hashIndex);
  const fragment = hashIndex < 0 ? "" : rest.slice(hashIndex);
  return `${scrubPiiText(base)}?${scrubQueryString(query)}${scrubPiiText(fragment)}`;
}

export function scrubPiiData(value, depth = 0, parentKey = null) {
  if (depth > 20) return PII_FILTERED;
  if (Array.isArray(value)) {
    return value.map((item) => scrubPiiData(item, depth + 1, parentKey));
  }
  if (value && typeof value === "object") {
    // A new object: never mutate data the app may still be using.
    const out = {};
    Object.keys(value).forEach((key) => {
      out[key] = isSensitiveKey(key)
        ? PII_FILTERED
        : scrubPiiData(value[key], depth + 1, key);
    });
    return out;
  }
  if (typeof value === "string" && isNumericKey(parentKey) && PLAIN_NUMBER.test(value.trim())) {
    return value;
  }
  // Numbers, booleans and null are left as they are.
  return scrubPiiText(value);
}

function scrubBody(data) {
  if (typeof data !== "string") return scrubPiiData(data);
  const trimmed = data.trim();
  if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
    try {
      return JSON.stringify(scrubPiiData(JSON.parse(trimmed)));
    } catch (_error) {
      return scrubPiiText(data);
    }
  }
  return data.includes("=") ? scrubQueryString(data) : scrubPiiText(data);
}

function scrubFrames(stacktrace) {
  (stacktrace?.frames || []).forEach((frame) => {
    if (frame && frame.vars) frame.vars = scrubPiiData(frame.vars);
  });
}

export function scrubPiiEvent(event) {
  if (!event || typeof event !== "object") return event;
  try {
    return scrubPiiEventUnsafe(event);
  } catch (_error) {
    // Never send what we could not scrub; never drop the event either.
    return {
      event_id: event.event_id,
      type: event.type,
      level: event.level,
      timestamp: event.timestamp,
      start_timestamp: event.start_timestamp,
      platform: event.platform,
      environment: event.environment,
      release: event.release,
      message: "Event content withheld: the PII scrubber failed.",
      ...(event.type === "transaction" ? { spans: [] } : {}),
    };
  }
}

function scrubPiiEventUnsafe(event) {
  if (event.request && typeof event.request === "object") {
    event.request = { ...event.request };
  }
  const request = event.request;
  if (request && typeof request === "object") {
    if ("url" in request) request.url = scrubPiiUrl(request.url);
    if ("query_string" in request) {
      request.query_string = typeof request.query_string === "string"
        ? scrubQueryString(request.query_string)
        : scrubPiiData(request.query_string);
    }
    if ("data" in request) request.data = scrubBody(request.data);
    ["headers", "cookies"].forEach((key) => {
      if (key in request) request[key] = scrubPiiData(request[key]);
    });
  }
  if ("message" in event) event.message = scrubPiiText(event.message);
  (event.exception?.values || []).forEach((value) => {
    if (!value) return;
    value.value = scrubPiiText(value.value);
    scrubFrames(value.stacktrace);
  });
  const crumbs = Array.isArray(event.breadcrumbs)
    ? event.breadcrumbs
    : event.breadcrumbs?.values || [];
  crumbs.forEach((crumb) => {
    if (!crumb) return;
    crumb.message = scrubPiiText(crumb.message);
    if (crumb.data) {
      crumb.data = scrubPiiData(crumb.data);
      if (typeof crumb.data.url === "string") crumb.data.url = scrubPiiUrl(crumb.data.url);
    }
  });
  ["extra", "contexts", "tags", "user"].forEach((key) => {
    if (key in event) event[key] = scrubPiiData(event[key]);
  });
  if ("transaction" in event) event.transaction = scrubPiiUrl(event.transaction);
  (event.spans || []).forEach((span) => {
    if (!span) return;
    span.description = scrubPiiUrl(span.description);
    if (span.data) span.data = scrubPiiData(span.data);
  });
  return event;
}

// Every outgoing event: the joint co-sign token, then PII.
export function scrubSentryEvent(event) {
  return scrubPiiEvent(scrubJointCosignEvent(event));
}

export function sentryBeforeSendTransaction(event) {
  return scrubSentryEvent(event);
}

export function sentryBeforeSend(event, hint) {
  const result = filterSentryEvent(scrubJointCosignEvent(event), hint);
  // Scrub last: the Axios preparation copies the server's error message
  // into contexts.api_error.
  return result === null ? null : scrubPiiEvent(result);
}

function filterSentryEvent(event, hint) {
  const originalException = hint?.originalException;
  const axiosEvent = prepareAxiosSentryEvent(event, hint);
  if (axiosEvent === null) {
    return null;
  }
  if (originalException?.isAxiosError) {
    return markFrontendEvent(axiosEvent);
  }

  if (
    isMetaMaskNoise(event, hint) ||
    isExpectedRouterNavigationFailure(event, hint) ||
    isResizeObserverLoopError(event, hint) ||
    isThirdPartyScriptNoise(event) ||
    isExtensionScriptNoise(event) ||
    isBooleanPromiseRejection(event, hint) ||
    isInjectedNativeBridgeNoise(event, hint)
  ) {
    return null;
  }

  if (isUnleashMetricsNoise(event, hint)) {
    return null;
  }

  if (isBrowserObjectLookupPromiseNoise(event, hint)) {
    return markFrontendEvent(
      markLowPriorityEvent(axiosEvent, "browser_object_lookup")
    );
  }

  if (isChunkLoadError(originalException) || isChunkLoadError(event)) {
    return null;
  }

  if (isNonActionableNetworkNoise(axiosEvent, hint)) {
    return null;
  }

  return markFrontendEvent(axiosEvent);
}
